import {call, put, select, takeLatest} from 'redux-saga/effects';

//Actions
import * as actions from '../../actions';

//Types
import {AxiosResponse} from 'axios';
import JSONFormData from '../../../utils/JSONFormData';
import {Account, DialingRule} from '../../../services/endpoints';
import {api} from '../../services/axios';
import {ReduxState} from '../../types';
import {ExtensionType} from '../../types/Extension';
import {ActionType} from 'typesafe-actions';
import {RingGroupType} from '../../types/RingGroup';
import {editServiceFeatures} from '../extensions/serviceFeatures/saga';
import {ServiceFeature, ServiceFeatureName,} from '../../types/ServiceFeature';
import {DialingRuleInfo} from '../../types/DialingRule';
import {showErrorToast} from '../../../utils/showToast';
import i18n from '../../../services/i18n';

export function* getAccountPanelAccountDetails() {
    try {
        const {session_id, csrf_token} = yield select(
            (state: ReduxState) => state.auth,
        );

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams({
            detailed_info: 1,
            limit_alias_did_number_list: 100
        });

        const response: AxiosResponse<{
            account_info: ExtensionType;
        }> = yield api.post(Account.GetAccountInfo, body);

        yield put(actions.getAccountPanelAccountDetails.success(response.data.account_info));
    } catch (err) {
        yield put(actions.getAccountPanelAccountDetails.failure());
    }
}

export function* getLastWrapUpActiveCall(action: ActionType<typeof actions.getLastWrapUpActiveCall.request>) {
    try {
        const {session_id, csrf_token} = yield select(
            (state: ReduxState) => state.auth,
        );

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams(action.payload);

        const response: AxiosResponse<{
            active_session_info: {
                release_time?: string,
                huntgroup_id?: string
            };
        }> = yield api.post(Account.GetLastWrapUpActiveCall, body);

        if(response.data.active_session_info?.huntgroup_id) {
            const response2: AxiosResponse<{
                huntgroup_list: RingGroupType[];
            }> = yield api.post(Account.GetAccountHuntGroupsList, body);

            const ringGroup = response2?.data?.huntgroup_list?.find(e => e.id == response.data.active_session_info?.huntgroup_id);

            yield put(actions.getLastWrapUpActiveCall.success({
                release_time: response.data.active_session_info?.release_time,
                call_wrap_up_time: ringGroup?.call_wrap_up_time,
                wrap_up_extend_time: ringGroup?.wrap_up_extend_time
            }));
        }
        else if(response.data.active_session_info?.release_time) {
            yield put(actions.getLastWrapUpActiveCall.success({
                release_time: response.data.active_session_info?.release_time
            }));
        }
        else {
            yield put(actions.getLastWrapUpActiveCall.success(undefined));
        }
    } catch (err) {
        yield put(actions.getLastWrapUpActiveCall.failure());
    }
}

export function* setWrapUpTime(action: ActionType<typeof actions.setWrapUpTime.request>) {
    try {
        const {session_id, csrf_token} = yield select(
            (state: ReduxState) => state.auth,
        );

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams(action.payload);

        yield api.post(Account.SetWrapUpTime, body);
        action.payload.callback?.();

        yield put(actions.setWrapUpTime.success());
    } catch (err) {
        yield put(actions.setWrapUpTime.failure());
    }
}

export function* updateForwardingModel(action: ActionType<typeof actions.updateForwardingModel.request>) {
    try {
        yield editServiceFeatures(
            action.payload.i_account,
            action.payload.serviceFeatures,
        );

        action.payload.callback?.();

        yield put(actions.updateForwardingModel.success());
    } catch (err) {
        yield put(actions.updateForwardingModel.failure());
    }
}

export function* getAccountServiceCodes() {
    try {
        const {session_id, csrf_token} = yield select(
            (state: ReduxState) => state.auth,
        );

        const accountInfoBody = new JSONFormData(session_id, csrf_token);
        accountInfoBody.setParams({detailed_info: 0});

        const accountInfoRes: AxiosResponse<{account_info: ExtensionType}> =
            yield api.post(Account.GetAccountInfo, accountInfoBody);

        const serviceFeatures: ServiceFeature[] =
            accountInfoRes.data.account_info?.service_features ?? [];

        const voiceDialing = serviceFeatures.find(
            (f) => f.name === ServiceFeatureName.VoiceDialing,
        );
        const iDialRule = voiceDialing?.attributes?.find(
            (a) => a.name === 'i_dial_rule',
        )?.effective_values?.[0];
        const voiceDialingEnabled =
            voiceDialing?.effective_flag_value === 'Y';

        if (!voiceDialingEnabled || iDialRule == null) {
            yield put(
                actions.getAccountServiceCodes.success({
                    dialingRule: null,
                    serviceFeatures,
                }),
            );
            return;
        }

        const ruleBody = new JSONFormData(session_id, csrf_token);
        ruleBody.setParams({
            i_dialing_rule: Number(iDialRule),
            check_usage: 1,
        });

        const ruleRes: AxiosResponse<{dialing_rule_info: DialingRuleInfo}> =
            yield api.post(DialingRule.GetDialingRuleInfo, ruleBody);

        yield put(
            actions.getAccountServiceCodes.success({
                dialingRule: ruleRes.data.dialing_rule_info,
                serviceFeatures,
            }),
        );
    } catch (err) {
        yield put(actions.getAccountServiceCodes.failure());
    }
}

const serviceCodesSaveErrorKey = (faultcode?: string) => {
    if (faultcode?.includes('sf.call_recording.validation_failed')) {
        return 'screens:accountDashboard.callRecordingCannotBeChanged';
    }
    if (faultcode?.includes('service_features.permission_denied')) {
        return 'common:limitedPermissionToPage';
    }
    return 'common:cantSaveChanges';
};

export function* updateAccountServiceCodesFeature(
    action: ActionType<typeof actions.updateAccountServiceCodesFeature.request>,
) {
    try {
        const {featureName, newValue} = action.payload;

        const accountDetails: ExtensionType | undefined = yield select(
            (s: ReduxState) => s.accountControlPanel.accountDetails,
        );
        const features: ServiceFeature[] = yield select(
            (s: ReduxState) =>
                s.accountControlPanel.accountServiceCodesFeatures ?? [],
        );

        const i_account = accountDetails?.i_account;
        const target = features.find((f) => f.name === featureName);

        if (!i_account || !target) {
            yield put(actions.updateAccountServiceCodesFeature.failure());
            return;
        }

        let updated: ServiceFeature;
        if (featureName === ServiceFeatureName.CallRecording) {
            const onDemand = newValue ? 'Y' : 'N';
            // The backend saves only `values`, which are empty or stale while
            // the feature is inherited, and resets any omitted attribute.
            const attributes = (target.attributes ?? []).map((a) =>
                a.name === 'on_demand'
                    ? {...a, values: [onDemand], effective_values: [onDemand]}
                    : {...a, values: a.effective_values ?? a.values},
            );
            // The backend applies attributes only when the account-level flag
            // is 'Y' ('~'/'N' silently drop them), so both directions send 'Y'.
            updated = {
                ...target,
                flag_value: 'Y',
                effective_flag_value: 'Y',
                attributes,
            };
        } else {
            updated = {
                ...target,
                flag_value: newValue ? 'Y' : 'N',
                effective_flag_value: newValue ? 'Y' : 'N',
            };
        }

        yield call(editServiceFeatures, i_account, [updated]);

        yield put(actions.updateAccountServiceCodesFeature.success());
        yield put(actions.getAccountPanelAccountDetails.request());
        yield put(actions.getAccountServiceCodes.request());
    } catch (err: any) {
        showErrorToast(
            i18n.t<string>(
                serviceCodesSaveErrorKey(err?.response?.data?.faultcode),
            ),
        );
        yield put(actions.updateAccountServiceCodesFeature.failure());
    }
}

export const accountControlPanelSaga = [
    takeLatest(actions.getAccountPanelAccountDetails.request, getAccountPanelAccountDetails),
    takeLatest(actions.getLastWrapUpActiveCall.request, getLastWrapUpActiveCall),
    takeLatest(actions.setWrapUpTime.request, setWrapUpTime),
    takeLatest(actions.updateForwardingModel.request, updateForwardingModel),
    takeLatest(actions.getAccountServiceCodes.request, getAccountServiceCodes),
    takeLatest(actions.updateAccountServiceCodesFeature.request, updateAccountServiceCodesFeature),
];
